import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { State } from '@/types';
import { emptyStringValues } from '@/utils/objects';

import {
  TFeaturedPeopleParametersType,
  TFeaturedPeopleType,
  TFeaturedPeopleTypeEnum,
} from '../types';

import { featuredPeopleQueryKeys } from './query-keys';
import { hasImagesToUpload, useUpdatePeopleImages } from './update-people';

export type TPostFeaturedPeopleParams = {
  name: string;
  state: State;
  description?: string | null;
  featured_people_type?: TFeaturedPeopleTypeEnum;
  parameters?: TFeaturedPeopleParametersType | null;
  alias?: string | null;
  active_detection?: boolean;
  main_logo?: File | string;
  logos?: File[] | string[];
};

export async function postFeaturedPeople(
  body: TPostFeaturedPeopleParams
): Promise<TFeaturedPeopleType> {
  return mediaManagerClient.post('/api/v1/peoples', emptyStringValues(body));
}

type MutationFn = typeof postFeaturedPeople;

type TUsePostFeaturedPeopleParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePostFeaturedPeople({ config = {} }: TUsePostFeaturedPeopleParams = {}) {
  const { t } = useTranslation();
  const { addErrorNotification, addSuccessNotification } = useNotificationStore();
  const { mutate: updatePeopleImages } = useUpdatePeopleImages();

  return useMutation({
    ...config,
    mutationFn: postFeaturedPeople,
    onError(error) {
      if (error.response?.status === 409) {
        addErrorNotification(t('Ya existe una persona de interés con ese nombre'));
      } else {
        addErrorNotification(t('No se ha podido crear la persona de interés'));
      }
      console.error(error);
    },
    onSuccess(featuredPeoples, variables) {
      addSuccessNotification(t('La persona de interés se ha creado correctamente'));
      queryClient.invalidateQueries({ queryKey: featuredPeopleQueryKeys.all });
      if (hasImagesToUpload(variables)) {
        updatePeopleImages({
          id: featuredPeoples.id,
          main_logo: variables.main_logo,
          logos: variables.logos,
        });
      }
    },
  });
}
